import React from 'react';
import { useNavigate } from 'react-router-dom';
import { pickVerifyPickerName } from '@/lib/pickVerifyShip';
import { CUSTOMER_PICKUP_LABEL, isCustomerPickupOrder } from '@/lib/customerPickup';
import './SalesOrderList.scss';

// Warehouse is on react-icons 4.x; RiArchiveStackLine only shipped in 5.x. Same Remix path the
// shipping, summary, and documents pages use for a bundle.
const RiArchiveStackLine = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="1em" height="1em" {...props}>
    <path d="M4 5H20V3H4V5ZM20 9H4V7H20V9ZM3 11H10V13H14V11H21V20C21 20.5523 20.5523 21 20 21H4C3.44772 21 3 20.5523 3 20V11ZM16 13V15H8V13H5V19H19V13H16Z" />
  </svg>
);

/** Bundle statuses under which a row is still "waiting with its siblings" rather than history. */
const LIVE_BUNDLE_STATUSES = ['open', 'shipping'];

function OrderDataRow({ salesOrder, usesTotes }: any) {
  const {
    id,
    vendorSalesOrderNumber,
    tote,
    shippingBundle,
    client: { companyName }
  } = salesOrder;
  const navigate = useNavigate();
  // Per render, from the live flag. `tote.number` does not exist on `type Tote`; dropped.
  const toteNumber = usesTotes ? tote?.name : null;
  // A set-aside order keeps vendorStatus 'shipping' and so sits in the Ship tab like any other;
  // the badge is what tells the operator it is being held for the client's other orders.
  const liveBundle =
    shippingBundle && LIVE_BUNDLE_STATUSES.includes(shippingBundle.status) ? shippingBundle : null;
  // Pick → Verify → Ship (Option 2): who holds the Pick claim. Read off the ORDER (its stamp wins
  // over the company setting), so it is null for every standard order on either kind of list.
  const pickerName = pickVerifyPickerName(salesOrder);
  // Marked for customer pickup in Hub: picked (and verified) as usual, then it skips Shipping.
  const customerPickup = isCustomerPickupOrder(salesOrder);

  return (
    <div
      className={['order-data-row', liveBundle && 'is-bundled', customerPickup && 'is-customer-pickup']
        .filter(Boolean)
        .join(' ')}
      onClick={() => navigate(`/sales-order-summary/${id}`)}
    >
      <div className="order-row-data">
        {vendorSalesOrderNumber}
        {!!toteNumber && (
          <>
            <span>
              <br /> Tote: {toteNumber}
            </span>
          </>
        )}
        {!!pickerName && (
          <span className="picking-by">
            <br /> Picking: {pickerName}
          </span>
        )}
        {!!liveBundle && (
          <>
            <br />
            <span className="bundle-chip" title={`Shipping bundle ${liveBundle.bundleNumber}`}>
              <RiArchiveStackLine aria-hidden="true" /> {liveBundle.bundleNumber}
            </span>
          </>
        )}
        {customerPickup && (
          <>
            <br />
            <span className="customer-pickup-chip" title="The customer collects this order — no shipping">
              {CUSTOMER_PICKUP_LABEL}
            </span>
          </>
        )}
      </div>
      <div>
        <span>{companyName}</span>
      </div>
    </div>
  );
}

// `isPicking` used to be threaded down to OrderDataRow, which never read it. Dropped rather than
// left dangling, since adding `usesTotes` to OrderDataRow's inferred props type turned that dead
// prop into a compile error.
//
// The four optional props exist for Pick → Verify → Ship (Option 2) companies, whose list has a
// Verify tab (see SalesOrders.tsx). Left unset — always, for an Option-1 company — each one is
// exactly today's behaviour: rows whose `vendorStatus === selectedStatus`, "in progress" meaning
// some unit is PICKED, and today's two section titles.
type SalesOrderListProps = {
  salesOrders: any[];
  selectedStatus: string;
  usesTotes: boolean;
  /** Which rows this tab shows. Default: `vendorStatus === selectedStatus`. */
  filter?: (salesOrder: any) => boolean;
  /** Which rows go in the top (in-progress) section. Default: some unit is PICKED. */
  isInProgress?: (salesOrder: any) => boolean;
  /** Top section title. Default: "partially picked", on every tab but Ship. */
  partialTitle?: string;
  /** Bottom section title. Default: "ready to pick" / "ready to ship". */
  readyTitle?: string;
};

function SalesOrderList({
  salesOrders,
  selectedStatus,
  usesTotes,
  filter,
  isInProgress,
  partialTitle,
  readyTitle
}: SalesOrderListProps) {
  const pickingSalesOrderList = salesOrders.filter(
    filter || (({ vendorStatus }) => vendorStatus === selectedStatus)
  );
  const inProgress = isInProgress || (({ partiallyPicked }) => !!partiallyPicked);

  return (
    <div className="sales-order-list">
      {partialTitle !== undefined ? (
        <div className="section-title">{partialTitle}</div>
      ) : (
        selectedStatus !== 'shipping' && <div className="section-title">partially picked</div>
      )}
      <div className="order-header-row">
        <div className="order-row-header">SO #</div>
      </div>
      <div className="partial-picked-wrapper">
        {pickingSalesOrderList
          .filter((salesOrder) => inProgress(salesOrder))
          .map((salesOrder) => (
            <OrderDataRow
              key={salesOrder.id}
              salesOrder={salesOrder}
              usesTotes={usesTotes}
            />
          ))}
      </div>
      <div className="section-title">
        {readyTitle !== undefined ? (
          readyTitle
        ) : (
          <>ready to {selectedStatus === 'picking' ? 'pick' : 'ship'}</>
        )}
      </div>
      {pickingSalesOrderList
        .filter((salesOrder) => !inProgress(salesOrder))
        .map((salesOrder) => (
          <OrderDataRow
            key={salesOrder.id}
            salesOrder={salesOrder}
            usesTotes={usesTotes}
          />
        ))}
    </div>
  );
}

export default SalesOrderList;
